import { useEventListener } from "usehooks-ts";
import { VideoPlayerRef } from "../components/VideoPlayer";
import { Action, ExpandedAction } from "../business-logic/ActionBehavior/Action";
import { ACTION_CONFIGS } from "../utils/actionMappings";

import { DetailedEvent } from "../requests/matchEvents";
import { useKeyboardShortcuts } from "../requests/keyboardShortcuts";
import { TAG_PREVIOUS_EVENT } from "../components/Account/KeyboardShortcutGrid";
import { useAddTagsSync } from "../requests/tags";

const isTextBoxEvent = (e: KeyboardEvent) => {
  const target = e?.target;
  if (target instanceof HTMLTextAreaElement || target instanceof HTMLInputElement) {
    if ([" ", ".", ",", "ArrowRight", "ArrowLeft"].includes(e.key) && target.value === "") {
      e.preventDefault();
      return false;
    }
    return true;
  }
  return false;
};

export const useVideoPlayerKeyboardShortcuts = (video: VideoPlayerRef) => {
  useEventListener("keydown", (e: KeyboardEvent) => {
    // Don't trigger keyboard shortcuts if typing in a text box.
    if (isTextBoxEvent(e)) {
      return;
    }

    switch (e.key) {
      case " ":
        video?.toggle();
        // This prevents the space bar key from scrolling down
        e.preventDefault();
        break;
      case "ArrowLeft":
        video?.skipForward(-5000);
        break;
      case "ArrowRight":
        video?.skipForward(5000);
        break;
      case "/":
        // Focus tag input for last event - handled by useActionKeyboardShortcuts
        break;
      case ".":
        video?.skipForward(15000);
        break;
      case ",":
        video?.skipForward(-15000);
        break;
    }
  });
};

export const useActionKeyboardShortcuts = (
  isActionButtonsEnabled: boolean,
  previousEvents: DetailedEvent[],
  pseudoEventActions: Set<ExpandedAction>,
  onActionButtonClick: (action: Action, tags: string[]) => void,
  isSelectedAction: boolean,
  removeSelectedAction: () => void
) => {
  const customKeyboardShortcuts = useKeyboardShortcuts();
  const addTags = useAddTagsSync();

  const handlePreviousEventKeyboardShortcuts = (e: KeyboardEvent) => {
    // Handle shortcuts for previous event.
    const previousEventShortcuts =
      customKeyboardShortcuts?.filter(
        (shortcut) => shortcut.action === TAG_PREVIOUS_EVENT || shortcut.action === null
      ) ?? [];
    const keyboardShortcut = previousEventShortcuts.find(
      (previousEventKeyboardShortcut) => previousEventKeyboardShortcut.shortcut === e.key
    );
    if (keyboardShortcut && previousEvents.length > 0 && keyboardShortcut.tags.length > 0) {
      addTags(
        // TODO: Is previousEvents[0] necessarily the last event tagged?
        previousEvents[0],
        keyboardShortcut.tags.map((tag) => tag.value)
      );
    }
  };

  const handleNewEventKeyboardShortcuts = (e: KeyboardEvent) => {
    // Handle shortcuts for creating new event.
    const newEventShortcuts =
      customKeyboardShortcuts?.filter(
        (shortcut) => shortcut.action !== TAG_PREVIOUS_EVENT && shortcut.action !== null
      ) ?? [];
    const keyboardShortcut = newEventShortcuts.find(
      (newEventShortcut) => newEventShortcut.shortcut === e.key
    );
    if (
      keyboardShortcut &&
      isActionButtonsEnabled &&
      newEventShortcuts.length > 0 &&
      ACTION_CONFIGS[keyboardShortcut.action as Action]?.isValid(
        previousEvents,
        pseudoEventActions,
        ACTION_CONFIGS
      )
    ) {
      onActionButtonClick(
        keyboardShortcut.action as Action,
        keyboardShortcut.tags.map((tag) => tag.value)
      );
    }
  };

  const handleTagInputShortcut = (e: KeyboardEvent) => {
    if (e.key === "/" && previousEvents.length > 0) {
      e.preventDefault();

      const tagButtons = document.querySelectorAll(
        'button svg[data-testid="LocalOfferOutlinedIcon"]'
      );

      if (tagButtons.length > 0) {
        const tagButton = tagButtons[0].closest("button") as HTMLButtonElement;
        if (tagButton) {
          tagButton.click();
        }
      }
    }
  };

  useEventListener("keydown", (e: KeyboardEvent) => {
    if (isTextBoxEvent(e)) {
      return;
    }

    if (e.key === "Escape" && isSelectedAction) {
      removeSelectedAction();
      return;
    }

    handleTagInputShortcut(e);
    handlePreviousEventKeyboardShortcuts(e);
    handleNewEventKeyboardShortcuts(e);
  });
};
